import { useDispatch, useSelector } from 'react-redux';
import TransportationProviderSelector from '@views/components/Selectors/AsynchSearch/TransportationProviderSelector';
import { Col, Form, Row } from 'antd';

import { CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE } from '@/contants/ClaimsEnums';
import { setWarningModal } from '@/redux/common/CommonActions';
import DatePicker from '@/views/components/Inputs/DatePicker';
import FormInput from '@/views/components/Inputs/FormInput';
import FormInputNumber from '@/views/components/Inputs/FormInputNumber';
import FormTextArea from '@/views/components/Inputs/FormTextArea';
import HiddenFormInput from '@/views/components/Inputs/HiddenFormInput';
import PostalCodeInput from '@/views/components/Selectors/PostalCodeInput';
import { CountrySelect, CurrencySelect, StateSelect } from '@/views/screens/common';
import { ShipmentBranchSelect } from '@/views/screens/Shipment';

import {
  PreClaimsReasonSelect,
  PreClaimsReasonTypeSelect,
  PreClaimsShipperConsigneeShortName,
  PreClaimsTypeSelect,
} from '../../../selects';

const PreClaimsDetailsForm = ({
  detailsForm,
  editMode = true,
  initiateSubmitProcess,
  onFieldsChange,
  initialValues,
}) => {
  const { selectedCompany } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const consigneeCountry = Form.useWatch('consigneeCountry', detailsForm);
  const shipperCountry = Form.useWatch('shipperCountry', detailsForm);

  const handleConsigneeZipEntry = (data) => {
    detailsForm.setFieldsValue({
      consigneeCity: data?.city,
      consigneeState: data?.state,
      consigneeZip: data?.zip,
    });
  };

  const handleShipperZipEntry = (data) => {
    detailsForm.setFieldsValue({
      shipperCity: data?.city,
      shipperState: data?.state,
      shipperZip: data?.zip,
    });
  };

  const handleConsigneeFetch = (type, data) => {
    let consigneeData = {};
    consigneeData.countryCode = initialValues?.consigneeCountry;
    if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.SUCCESS) {
      consigneeData = data;
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.EMPTY) {
      dispatch(setWarningModal({ description: 'No data found', open: true }));
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.CLEAR) {
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.ERROR) {
      dispatch(
        setWarningModal({
          description: 'Error while fetching data',
          open: true,
        }),
      );
    }
    detailsForm.setFieldsValue({
      consigneeAddr1: consigneeData?.addr1,
      consigneeAddr2: consigneeData?.addr2,
      consigneeCity: consigneeData?.city,
      consigneeCountry: consigneeData?.countryCode,
      consigneeKey: consigneeData?.claimShipRecKey,
      consigneeName: consigneeData?.consigneeShipperName,
      consigneeState: consigneeData?.state,
      consigneeZip: consigneeData?.zip,
    });
  };

  const consigneeKey = Form.useWatch('consigneeKey', detailsForm);

  const handleShipperFetch = (type, data) => {
    let consigneeData = {};
    consigneeData.countryCode = initialValues?.shipperCountry;
    if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.SUCCESS) {
      consigneeData = data;
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.EMPTY) {
      dispatch(setWarningModal({ description: 'No data found', open: true }));
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.CLEAR) {
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.ERROR) {
      dispatch(
        setWarningModal({
          description: 'Error while fetching data',
          open: true,
        }),
      );
    }
    detailsForm.setFieldsValue({
      originKey: consigneeData?.claimShipRecKey,
      shipperAddr1: consigneeData?.addr1,
      shipperAddr2: consigneeData?.addr2,
      shipperCity: consigneeData?.city,
      shipperCountry: consigneeData?.countryCode,
      shipperName: consigneeData?.consigneeShipperName,
      shipperState: consigneeData?.state,
      shipperZip: consigneeData?.zip,
    });
  };

  const originKey = Form.useWatch('originKey', detailsForm);

  return (
    <Form name="detailsForm" form={detailsForm} onFieldsChange={onFieldsChange} initialValues={initialValues}>
      <Row gutter={15}>
        <Col sm={6}>
          <CurrencySelect
            margin="mb-15"
            editMode={editMode}
            name="currencyCode"
            tabIndex="0"
            rules={[
              {
                message: 'Please select a Currency',
                required: true,
              },
            ]}
          />
          <TransportationProviderSelector
            margin="mb-15"
            form={detailsForm}
            showPopupFilter
            label="Transportation Provider"
            name="carrierKey"
            // inputValue={carrierKey}
            // carrierName={carrierName}
            // onChange={handleCarrierChange}
            disableOpen
            tabIndex="0"
            rules={[
              {
                message: 'Please select a Transportation Provider',
                required: true,
              },
            ]}
          />
          <PreClaimsShipperConsigneeShortName
            margin="mb-15"
            label="Consignee Short Name"
            name="consigneeShortName"
            editMode={editMode}
            onFetch={handleConsigneeFetch}
            tabIndex="0"
            maxLength={9}
          />
          <FormInput
            margin="mb-15"
            label="Consignee"
            name="consigneeName"
            editMode={editMode && !consigneeKey}
            tabIndex="0"
            maxLength={30}
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                message: 'Please enter the Consignee Name',
                required: true,
              },
            ]}
          />
          <FormTextArea
            margin="mb-15"
            label="Address"
            name="consigneeAddr1"
            editMode={editMode && !consigneeKey}
            tabIndex="0"
            rows={2}
            rules={[
              {
                message: 'Please enter the Address',
                required: true,
              },
            ]}
            maxLength={35}
          />
          <FormTextArea
            margin="mb-15"
            label="Address 1"
            name="consigneeAddr2"
            editMode={editMode && !consigneeKey}
            tabIndex="0"
            rows={2}
            maxLength={35}
          />
          <CountrySelect
            margin="mb-15"
            label="Country"
            name="consigneeCountry"
            editMode={editMode && !consigneeKey}
            tabIndex="0"
            rules={[
              {
                message: 'Please select a country',
                required: true,
              },
            ]}
          />
          <PostalCodeInput
            margin="mb-15"
            label="Postal Code"
            name="consigneeZip"
            editMode={editMode && !consigneeKey}
            tabIndex="0"
            rules={[
              {
                message: 'Please enter Consignee postal code',
                required: true,
              },
            ]}
            countryCode={consigneeCountry}
            handleZipDetails={handleConsigneeZipEntry}
            maxLength={10}
          />
          <StateSelect
            margin="mb-15"
            label="State"
            name="consigneeState"
            editMode={false}
            rules={[
              {
                message: 'Please select a state',
                required: true,
              },
            ]}
            countryCode={consigneeCountry}
          />
          <FormInput
            margin="mb-0"
            label="City"
            name="consigneeCity"
            editMode={false}
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                message: 'Please enter city',
                required: true,
              },
            ]}
          />
        </Col>
        <Col sm={6}>
          <PreClaimsTypeSelect
            name="claimTypeKey"
            margin="mb-10"
            tabIndex="0"
            rules={[
              {
                message: 'Please select a claim Type',
                required: true,
              },
            ]}
          />
          <DatePicker
            margin="mb-15"
            label="Ship Date"
            name="dateShipped"
            tabIndex={0}
            rules={[
              {
                message: 'Please select Ship Date',
                required: true,
              },
            ]}
            disabledTargetDate={new Date().toISOString()}
            disableFuture
          />
          <FormInput
            margin="mb-15"
            label="Pieces"
            name="claimNoOfPieces"
            editMode={editMode}
            inputType="number"
            tabIndex="0"
          />
          <PreClaimsReasonTypeSelect
            name="reasonType"
            margin="mb-10"
            tabIndex="0"
            rules={[
              {
                message: 'Please select a claim Reason Type',
                required: true,
              },
            ]}
          />
        </Col>
        <Col sm={6}>
          <ShipmentBranchSelect
            label="Branch"
            name="branchKey"
            tabIndex="0"
            margin="mb-10"
            rules={[
              {
                message: 'Please select a claim Branch',
                required: true,
              },
            ]}
            customerId={selectedCompany.companyId}
          />

          <PreClaimsShipperConsigneeShortName
            margin="mb-15"
            label="Shipper Short Name"
            name="shipperShortName"
            editMode={editMode}
            placeholder="Enter shipper short name"
            onFetch={handleShipperFetch}
            tabIndex="0"
            maxLength={9}
          />
          <FormInput
            margin="mb-15"
            label="Shipper"
            name="shipperName"
            editMode={editMode && !originKey}
            tabIndex="0"
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                message: 'Please enter the Shipper Name',
                required: true,
              },
            ]}
            maxLength={30}
          />
          <FormTextArea
            margin="mb-15"
            label="Address"
            name="shipperAddr1"
            editMode={editMode && !originKey}
            tabIndex="0"
            rows={2}
            rules={[
              {
                message: 'Please enter the Address',
                required: true,
              },
            ]}
            maxLength={35}
          />
          <FormTextArea
            margin="mb-15"
            label="Address 1"
            name="shipperAddr2"
            editMode={editMode && !originKey}
            tabIndex="0"
            rows={2}
            maxLength={35}
          />
          <CountrySelect
            margin="mb-15"
            label="Country"
            name="shipperCountry"
            editMode={editMode && !originKey}
            tabIndex="0"
            rules={[
              {
                message: 'Please select a country',
                required: true,
              },
            ]}
          />
          <PostalCodeInput
            margin="mb-15"
            label="Postal Code"
            name="shipperZip"
            editMode={editMode && !originKey}
            tabIndex="0"
            rules={[
              {
                message: 'Please enter Shipper Postal Code',
                required: true,
              },
            ]}
            countryCode={shipperCountry}
            handleZipDetails={handleShipperZipEntry}
            maxLength={10}
          />
          <StateSelect
            margin="mb-15"
            label="State"
            name="shipperState"
            editMode={false}
            rules={[
              {
                message: 'Please select a state',
                required: true,
              },
            ]}
            countryCode={shipperCountry}
          />
          <FormInput
            margin="mb-0"
            label="City"
            name="shipperCity"
            editMode={false}
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                message: 'Please enter city',
                required: true,
              },
            ]}
          />
        </Col>
        <Col sm={6}>
          <FormInput
            margin="mb-15"
            label="Invoice Number"
            name="invoiceNo"
            editMode={editMode}
            tabIndex="0"
            maxLength={25}
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                message: 'Please enter Invoice Number',
                required: true,
              },
            ]}
            onBlur={() => initiateSubmitProcess(true, 'invoiceNo')}
          />
          <FormInput
            margin="mb-15"
            label="Freight Bill / Pro Number"
            name="claimProNo"
            tabIndex="0"
            editMode={editMode}
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                message: 'Please enter Freight Bill / Pro Number',
                required: true,
              },
            ]}
            maxLength={25}
            onBlur={() => initiateSubmitProcess(true, 'claimProNo')}
          />
          <FormInputNumber
            margin="mb-15"
            label="Chargeable Weight"
            name="cargoShipmentWeight"
            editMode={editMode}
            tabIndex="0"
            inputType="number"
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                message: 'Please enter Chargeable Weight',
                required: true,
              },
              () => ({
                validator(_, value) {
                  if (value <= 0) {
                    return Promise.reject(new Error('Chargeable weight should be grater than zero'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          />
          <PreClaimsReasonSelect
            name="reasonCode"
            tabIndex="0"
            margin="mb-10"
            rules={[
              {
                message: 'Please select a claim Reason',
                required: true,
              },
            ]}
          />
        </Col>
        <HiddenFormInput name="consigneeKey" />
        <HiddenFormInput name="originKey" />
        <HiddenFormInput name="claimStatusCode" />
      </Row>
    </Form>
  );
};

export default PreClaimsDetailsForm;
